import { api } from './client'
import type { Reason } from '../lib/visitorsEngagement'
import { rangeQuery, type DateRange } from '../lib/visitorsRange'

export type Step = 'visit' | 'engaged' | 'considered' | 'checkout' | 'paid'
export interface FunnelRow { step: Step; sessions: number; rate_from_previous: number | null }
export interface Traffic { visitors: number; sessions: number; engaged_sessions: number; engaged_rate: number; avg_engaged_ms: number; pages_per_session: number; returning_share: number }
export interface Revenue {
  orders: number; gross_cents: number; refunds_cents: number; net_cents: number; aov_cents: number
  new_members: number; gifts: number; promo_orders: number; by_payment: Record<string, number>
  renewals: { orders: number; cents: number }
  offline: { orders: number; cents: number }
}
export interface Summary {
  range: { from: string; to: string }
  raw: { page_views: number; sessions: number; human: number; unconfirmed: number; automated: number; bot_share: number }
  traffic: Traffic; funnel: FunnelRow[]; revenue: Revenue
  freshness: { last_event_at: string | null; unsessioned: number }
  previous: { traffic: Traffic; funnel: FunnelRow[]; revenue: Revenue }
}
export interface NowResp {
  active_visitors: number; unconfirmed: number; members_in_issue: number; in_checkout: number
  top_pages: { path: string; title: string | null; visitors: number; source: string | null }[]
}
export interface DayRow { day: string; visitors: number; sessions: number; engaged_sessions: number; paid: number }
export interface TimeseriesResp { days: DayRow[]; previous: DayRow[] }
export type Dimension = 'channel' | 'device' | 'country' | 'landing' | 'campaign'
export interface BreakdownRow { key: string; visitors: number; sessions: number; engaged: number; considered: number; checkout: number; paid: number; conversion: number }
export interface BreakdownResp { dimension: Dimension; rows: BreakdownRow[] }
export interface FunnelStepResp {
  step: Step; sessions: number
  by_channel: { key: string; sessions: number; next_step_sessions: number }[]
  by_flow: { flow: string; started: number; reached_payment: number; paid: number }[]
  abandoned_fields: { field: string; sessions: number }[]
}
export interface SessionRow {
  session_id: string; visitor_id: string; subscriber_id: number | null; started_at: string; ended_at: string
  landing_path: string | null; exit_path: string | null; page_count: number; engaged_ms: number
  channel: string | null; device: string | null; country: string | null; max_funnel_step: Step | null
  order_id: number | null; verdict: 'human' | 'unconfirmed' | 'automated'; verdict_reason: string | null
}
export interface SessionsResp { items: SessionRow[]; total: number }
export interface EventRow { id: number; ts: string; type: string; source: string; path: string; template: string | null; props: Record<string, unknown>; referrer_host: string | null; utm_source: string | null; utm_medium: string | null; utm_campaign: string | null }
/** The email a visit came from. id/subject/sent_at only for viewers of named member history. */
export interface FromEmail { id?: number; subject?: string | null; send_type: string; campaign_id: number | null; sent_at?: string }
export interface SessionDetail extends SessionRow { events: EventRow[]; from_email: FromEmail | null }
export interface EmailCampaignRow { campaign: string; sessions: number; human_sessions: number; engaged: number; paid: number; emails: number; conversion: number }
export type EventDetail = Record<string, unknown> & { id: number; ts: string; type: string }
export interface Title { slug: string; name: string; has_data: boolean; first_event_at: string | null; last_event_at: string | null }
export interface EngagementResp {
  members: number; active_30d: number; active_7d: number; active_1d: number; active_share_30d: number
  reads_per_active: number; median_depth: number; edition_opens: number; edition_pages: number
  sign_ins: { password: number; magic_link: number; account_zip: number; sign_in_code: number }
  collecting: boolean; score_day: string | null
}
export interface DownloadIssue {
  issue_id: number; title: string; post_date: string | null; cover_image_url: string | null
  downloads: number; downloaders: number; opens: number; readers: number; download_share: number | null
}
export interface Downloader {
  subscriber_id: number | null; name: string | null; account_number: string | null; visitor_id: string | null
  member: boolean; downloads: number; issues: number[]; last_at: string
}
export interface DownloadsResp {
  downloads: number; downloaders: number; private_downloads: number; issues_downloaded: number; opens: number
  issues: DownloadIssue[]
  by_day: { day: string; downloads: number }[]
  by_device: { key: string; downloads: number }[]
  by_channel: { key: string; downloads: number }[]
  people: Downloader[]; people_truncated: boolean; issue_id: number | null
}
export interface AppResp {
  guide_views: number; clicks_by_placement: Record<string, number>; app_readers: number; app_share: number
}
export interface CohortRow { month: string; readers: number; retained: (number | null)[] }
export interface AtRiskItem {
  subscriber_id: number; renews_on: string; plan: string | null; auto_renew: boolean; expired_card: boolean | null
  score: number | null; score_30d_ago: number | null; trend: (number | null)[]
  last_read_at: string | null; reads_90d: number; email_opens_90d: number; reason: Reason
}
export interface AtRiskResp {
  items: AtRiskItem[]; total: number; collecting: boolean; score_day: string | null
  counts: { auto_renew_on: number; auto_renew_off: number; expired_card: number; card_unknown: number }
}
export type AutoRenewFilter = 'any' | 'on' | 'off' | 'expired_card'
export interface SegmentSaved { segment_id: number; slug: string; members: number }
export type TimelineKind = 'visit' | 'read' | 'edition' | 'download' | 'email_sent' | 'email_open' | 'email_click' | 'sign_in' | 'order' | 'refund' | 'renewal_due'
export interface TimelineEvent {
  at: string; kind: TimelineKind; title: string; detail: string
  link: { type: 'session' | 'event'; id: string | number } | null
}
export interface MemberTimeline {
  subscriber_id: number; renews_on: string | null; plan: string | null; auto_renew: boolean | null; status: string | null
  score: { today: number | null; trend: { day: string; score: number }[]; day: string | null; score_day: string | null; collecting: boolean
    reading_days: number | null; email_opens: number | null; email_clicks: number | null; engaged_ms: number | null }
  visitor_ids: string[]
  reading_grid: { week_start: string; engaged_ms: number }[]
  events: TimelineEvent[]
}
export interface VisitorTimeline { visitor_id: string; linked_subscriber_ids: number[]; sessions: SessionRow[] }
export interface SessionFilters { step?: Step; channel?: string; device?: string; country?: string; landing?: string; campaign?: string; verdict?: string
  exit?: string; active?: string; referrer_host?: string; channel_detail?: string; region?: string }

const qs = (o: Record<string, string | number | undefined>) =>
  Object.entries(o).filter(([, v]) => v !== undefined && v !== '').map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&')

export const visitorsApi = {
  titles: () => api.get<Title[]>('/api/visitors/titles'),
  summary: (pub: string, r: DateRange) => api.get<Summary>(`/api/visitors/summary?${rangeQuery(pub, r)}`),
  now: (pub: string) => api.get<NowResp>(`/api/visitors/now?publication=${encodeURIComponent(pub)}`),
  timeseries: (pub: string, r: DateRange) => api.get<TimeseriesResp>(`/api/visitors/timeseries?${rangeQuery(pub, r)}`),
  breakdown: (pub: string, r: DateRange, dim: Dimension) => api.get<BreakdownResp>(`/api/visitors/breakdown?${rangeQuery(pub, r)}&dimension=${dim}`),
  emails: (pub: string, r: DateRange) => api.get<{ rows: EmailCampaignRow[] }>(`/api/visitors/emails?${rangeQuery(pub, r)}`),
  funnelStep: (pub: string, r: DateRange, step: Step) => api.get<FunnelStepResp>(`/api/visitors/funnel/${step}?${rangeQuery(pub, r)}`),
  sessions: (pub: string, r: DateRange, f: SessionFilters, limit = 50, offset = 0) =>
    api.get<SessionsResp>(`/api/visitors/sessions?${rangeQuery(pub, r)}&${qs({ ...f, active: undefined, active_minutes: f.active, limit, offset })}`),
  session: (id: string) => api.get<SessionDetail>(`/api/visitors/sessions/${encodeURIComponent(id)}`),
  engagement: (pub: string, r: DateRange) => api.get<EngagementResp>(`/api/visitors/engagement?${rangeQuery(pub, r)}`),
  cohorts: (pub: string) => api.get<{ rows: CohortRow[] }>(`/api/visitors/cohorts?publication=${encodeURIComponent(pub)}`),
  atRisk: (pub: string, within: number, autoRenew: AutoRenewFilter, limit = 50, offset = 0) =>
    api.get<AtRiskResp>(`/api/visitors/at-risk?${qs({ publication: pub, within, auto_renew: autoRenew, limit, offset })}`),
  saveAtRiskSegment: (b: { publication: string; within: number; auto_renew: AutoRenewFilter; name: string }) =>
    api.post<SegmentSaved>('/api/visitors/at-risk/segment', b),
  member: (pub: string, id: number) => api.get<MemberTimeline>(`/api/visitors/members/${id}?publication=${encodeURIComponent(pub)}`),
  visitor: (pub: string, id: string) => api.get<VisitorTimeline>(`/api/visitors/visitors/${encodeURIComponent(id)}?publication=${encodeURIComponent(pub)}`),
  event: (id: number) => api.get<EventDetail>(`/api/visitors/events/${id}`),
  downloads: (pub: string, r: DateRange, issueId?: number | null) =>
    api.get<DownloadsResp>(`/api/visitors/downloads?${rangeQuery(pub, r)}${issueId ? `&issue_id=${issueId}` : ''}`),
  app: (pub: string, r: DateRange) => api.get<AppResp>(`/api/visitors/app?${rangeQuery(pub, r)}`),
}
